import { supabase } from '../supabaseClient';
import { Team, TeamMember, TeamMemberStatus } from '../types/domain';
import { recordDevLocalStorageProgramCacheUse } from '../utils/devNetworkTransferMonitor';
import {
  clearMyTeamMembershipCache,
  loadMyTeamMembershipCache,
  saveMyTeamMembershipCache,
} from '../utils/myTeamMembershipCache';
import {
  buildMyMembershipVersion,
  clearTeamSettingsSnapshot,
} from '../utils/teamSettingsSnapshotCache';

interface TeamJoinResultRow {
  joined_team_id: string | null;
  member_status: TeamMemberStatus;
  join_message: string;
}

export interface TeamJoinResult {
  teamId: string | null;
  status: TeamMemberStatus;
  message: string;
}

export interface TeamShareImportResult {
  message: string;
}

const TEAM_SELECT_COLUMNS =
  'id, workspace_id, created_by, name, code, member_limit, auto_approve_members';

export async function getTeams(workspaceId: string): Promise<Team[]> {
  const { data, error } = await supabase
    .from('teams')
    .select(TEAM_SELECT_COLUMNS)
    .eq('workspace_id', workspaceId)
    .order('created_at', { ascending: true });

  // teams 테이블이 아직 없는 경우에는 조용히 빈 배열 반환 (초기 개발 단계 방어 로직)
  if (error) {
    if (error.message?.includes("Could not find the table 'public.teams'")) {
      // eslint-disable-next-line no-console
      console.warn('teams 테이블이 아직 없어 빈 배열을 반환합니다.');
      return [];
    }
    throw new Error(error.message);
  }

  return (data ?? []) as Team[];
}

export async function createTeam(
  workspaceId: string,
  input: { name: string; code: string; memberLimit: number | null }
): Promise<Team> {
  const { data: userData } = await supabase.auth.getUser();
  const userId = userData.user?.id;
  if (!userId) {
    throw new Error('로그인이 필요합니다.');
  }

  const { count: otherMembershipCount, error: otherMemErr } = await supabase
    .from('team_members')
    .select('team_id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .in('status', ['approved', 'pending']);
  if (otherMemErr) {
    throw new Error(otherMemErr.message);
  }
  if ((otherMembershipCount ?? 0) > 0) {
    throw new Error(
      '이미 소속된 팀이 있거나 가입 요청이 진행 중입니다. 기존 팀에서 나가거나 요청이 처리된 뒤 새 팀을 만들 수 있습니다.'
    );
  }

  const payload = {
    workspace_id: workspaceId,
    created_by: userId,
    name: input.name.trim(),
    code: input.code.trim(),
    member_limit: input.memberLimit,
  };

  const { data, error } = await supabase
    .from('teams')
    .insert(payload)
    .select(TEAM_SELECT_COLUMNS)
    .single();

  if (error || !data) {
    if (error?.message?.includes("Could not find the table 'public.teams'")) {
      throw new Error('팀 기능 초기 설정이 아직 완료되지 않았습니다. 잠시 후 다시 시도해 주세요.');
    }
    throw new Error(error?.message ?? '팀 생성에 실패했습니다.');
  }

  const { error: memberError } = await supabase.from('team_members').upsert(
    {
      team_id: data.id,
      user_id: userId,
      role: 'teacher',
      status: 'approved',
      is_active: false,
      approved_by: userId,
      approved_at: new Date().toISOString(),
      rejected_by: null,
      rejected_at: null,
    },
    { onConflict: 'team_id,user_id' }
  );

  if (memberError) {
    throw new Error(memberError.message);
  }

  const { data: switched, error: switchError } = await supabase.rpc('switch_active_team', {
    p_team_id: data.id,
  });
  if (switchError || switched !== true) {
    throw new Error(switchError?.message ?? '활성 팀 전환에 실패했습니다.');
  }

  await invalidateMyTeamMembershipCache();
  return data as Team;
}

export async function deleteTeam(teamId: string): Promise<void> {
  const { error } = await supabase.from('teams').delete().eq('id', teamId);
  if (error) {
    throw new Error(error.message ?? '팀 삭제에 실패했습니다.');
  }
  await invalidateMyTeamMembershipCache();
}

export async function updateTeam(
  teamId: string,
  patch: {
    name?: string;
    code?: string;
    memberLimit?: number | null;
    autoApproveMembers?: boolean;
  }
): Promise<Team> {
  const toUpdate: Record<string, unknown> = {};
  if (patch.name !== undefined) toUpdate.name = patch.name.trim();
  if (patch.code !== undefined) toUpdate.code = patch.code.trim();
  if (patch.memberLimit !== undefined) toUpdate.member_limit = patch.memberLimit;
  if (patch.autoApproveMembers !== undefined) {
    toUpdate.auto_approve_members = patch.autoApproveMembers;
  }

  const { data, error } = await supabase
    .from('teams')
    .update(toUpdate)
    .eq('id', teamId)
    .select(TEAM_SELECT_COLUMNS)
    .single();

  if (error || !data) {
    if (error?.message?.includes("Could not find the table 'public.teams'")) {
      throw new Error('팀 기능 초기 설정이 아직 완료되지 않았습니다. 잠시 후 다시 시도해 주세요.');
    }
    throw new Error(error?.message ?? '팀 수정에 실패했습니다.');
  }

  await invalidateMyTeamMembershipCache();
  return data as Team;
}

export async function requestJoinTeam(
  teamName: string,
  teamCode: string
): Promise<TeamJoinResult> {
  const { data, error } = await supabase.rpc('request_team_join', {
    p_team_name: teamName.trim(),
    p_team_code: teamCode.trim(),
  });

  if (error) {
    throw new Error(error.message);
  }

  const row = (Array.isArray(data) ? data[0] : data) as TeamJoinResultRow | null;
  if (!row) {
    throw new Error('팀 가입 요청 처리에 실패했습니다.');
  }

  await invalidateMyTeamMembershipCache();
  return {
    teamId: row.joined_team_id,
    status: row.member_status,
    message: row.join_message,
  };
}

export async function getTeamMembers(teamId: string): Promise<TeamMember[]> {
  const { data, error } = await supabase.rpc('list_team_members_with_profile', {
    p_team_id: teamId,
  });

  if (error) {
    throw new Error(error.message);
  }

  return (data ?? []) as TeamMember[];
}

export async function getPendingMembers(teamId: string): Promise<TeamMember[]> {
  const { data, error } = await supabase.rpc('list_team_members_with_profile', {
    p_team_id: teamId,
  });

  if (error) {
    throw new Error(error.message);
  }
  return ((data ?? []) as TeamMember[]).filter((row) => row.status === 'pending');
}

export async function approveMember(teamId: string, userId: string): Promise<void> {
  const { data, error } = await supabase.rpc('approve_team_member', {
    p_team_id: teamId,
    p_user_id: userId,
  });
  if (error) {
    throw new Error(error.message);
  }
  if (data !== true) {
    throw new Error('승인 처리에 실패했습니다.');
  }
  await invalidateMyTeamMembershipCache();
}

export async function rejectMember(teamId: string, userId: string): Promise<void> {
  const { data, error } = await supabase.rpc('reject_team_member', {
    p_team_id: teamId,
    p_user_id: userId,
  });
  if (error) {
    throw new Error(error.message);
  }
  if (data !== true) {
    throw new Error('거절 처리에 실패했습니다.');
  }
  await invalidateMyTeamMembershipCache();
}

export async function removeMember(teamId: string, userId: string): Promise<void> {
  const { data, error } = await supabase.rpc('remove_team_member', {
    p_team_id: teamId,
    p_user_id: userId,
  });
  if (error) {
    throw new Error(error.message);
  }
  if (data !== true) {
    throw new Error('팀원 제외 처리에 실패했습니다.');
  }
  await invalidateMyTeamMembershipCache();
}

export async function setTeamAutoApprove(teamId: string, enabled: boolean): Promise<void> {
  const { data, error } = await supabase.rpc('set_team_auto_approve', {
    p_team_id: teamId,
    p_enabled: enabled,
  });
  if (error) {
    const fallback = await supabase
      .from('teams')
      .update({ auto_approve_members: enabled })
      .eq('id', teamId);
    if (fallback.error) {
      throw new Error(fallback.error.message ?? error.message);
    }
    await invalidateMyTeamMembershipCache();
    return;
  }
  if (data !== true) {
    throw new Error('자동 등록 설정 변경 권한이 없습니다.');
  }
  await invalidateMyTeamMembershipCache();
}

export async function switchActiveTeam(teamId: string): Promise<void> {
  const { data, error } = await supabase.rpc('switch_active_team', {
    p_team_id: teamId,
  });
  if (error) {
    throw new Error(error.message);
  }
  if (data !== true) {
    throw new Error('활성 팀 전환에 실패했습니다.');
  }
  await invalidateMyTeamMembershipCache();
}

export async function getMyActiveTeamMembership(): Promise<{
  team: Team;
  membership: TeamMember;
} | null> {
  const { data: authData } = await supabase.auth.getUser();
  const userId = authData.user?.id;
  if (!userId) return null;

  const { data, error } = await supabase
    .from('team_members')
    .select(
      'team_id, user_id, role, status, is_active, joined_at, approved_at, rejected_at, team:teams(id, workspace_id, created_by, name, code, member_limit, auto_approve_members)'
    )
    .eq('user_id', userId)
    .eq('status', 'approved')
    .eq('is_active', true)
    .maybeSingle();

  if (error) {
    throw new Error(error.message);
  }

  if (!data || !data.team) return null;
  const resolvedTeam = Array.isArray(data.team) ? data.team[0] : data.team;
  if (!resolvedTeam) return null;
  return {
    team: resolvedTeam as Team,
    membership: data as unknown as TeamMember,
  };
}

async function invalidateMyTeamMembershipCache(): Promise<void> {
  const { data: authData } = await supabase.auth.getUser();
  const userId = authData.user?.id;
  if (userId) {
    clearMyTeamMembershipCache(userId);
    clearTeamSettingsSnapshot(userId);
  }
}

async function fetchMyMembershipHeadVersion(userId: string): Promise<string> {
  const { data, error } = await supabase
    .from('team_members')
    .select('team_id, role, status, is_active, joined_at, approved_at, rejected_at')
    .eq('user_id', userId)
    .in('status', ['approved', 'pending'])
    .order('joined_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return buildMyMembershipVersion(data);
}

/** 팀 메타 + 멤버(프로필 제외) 시그니처 — 풀 list_team_members_with_profile 생략용 */
export async function fetchTeamRosterVersion(teamId: string): Promise<string> {
  const tid = teamId.trim();
  if (!tid) return 'none';
  const [teamRes, membersRes] = await Promise.all([
    supabase
      .from('teams')
      .select('id, name, code, member_limit, auto_approve_members, created_by, updated_at')
      .eq('id', tid)
      .maybeSingle(),
    supabase
      .from('team_members')
      .select('user_id, role, status, is_active, joined_at, approved_at, rejected_at')
      .eq('team_id', tid)
      .in('status', ['approved', 'pending'])
      .order('joined_at', { ascending: true }),
  ]);
  if (teamRes.error) throw new Error(teamRes.error.message);
  if (membersRes.error) throw new Error(membersRes.error.message);
  const team = teamRes.data;
  if (!team) return `missing:${tid}`;
  const memberSig = (membersRes.data ?? [])
    .map(
      (m) =>
        `${m.user_id}:${m.role}:${m.status}:${m.is_active ? 1 : 0}:${m.joined_at ?? ''}:${m.approved_at ?? ''}:${m.rejected_at ?? ''}`,
    )
    .join(',');
  return [
    team.id,
    team.updated_at ?? '',
    team.name ?? '',
    team.code ?? '',
    team.member_limit ?? '',
    team.auto_approve_members ? 1 : 0,
    team.created_by ?? '',
    memberSig,
  ].join('|');
}

async function fetchMyCurrentTeamMembershipFromServer(userId: string): Promise<{
  team: Team | null;
  membership: TeamMember;
} | null> {
  const { data, error } = await supabase
    .from('team_members')
    .select(
      'team_id, user_id, role, status, is_active, joined_at, approved_at, rejected_at, team:teams(id, workspace_id, created_by, name, code, member_limit, auto_approve_members)'
    )
    .eq('user_id', userId)
    .in('status', ['approved', 'pending'])
    .order('joined_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    throw new Error(error.message);
  }

  if (!data) {
    saveMyTeamMembershipCache({
      userId,
      team: null,
      membership: null,
      membershipVersion: 'none',
      savedAt: Date.now(),
    });
    return null;
  }
  const resolvedTeam = Array.isArray(data.team) ? data.team[0] : data.team;
  const result = {
    team: resolvedTeam ? (resolvedTeam as Team) : null,
    membership: data as unknown as TeamMember,
  };
  saveMyTeamMembershipCache({
    userId,
    team: result.team,
    membership: result.membership,
    membershipVersion: buildMyMembershipVersion(data),
    savedAt: Date.now(),
  });
  return result;
}

export async function getMyCurrentTeamMembership(options?: {
  forceRefresh?: boolean;
}): Promise<{
  team: Team | null;
  membership: TeamMember;
} | null> {
  const { data: authData } = await supabase.auth.getUser();
  const userId = authData.user?.id;
  if (!userId) return null;

  if (!options?.forceRefresh) {
    const cached = loadMyTeamMembershipCache(userId);
    if (cached) {
      try {
        const headVersion = await fetchMyMembershipHeadVersion(userId);
        if (cached.membershipVersion === headVersion) {
          recordDevLocalStorageProgramCacheUse('팀멤버십·LS');
          if (!cached.membership) return null;
          return { team: cached.team, membership: cached.membership };
        }
      } catch {
        // head 실패 시 캐시 무시하고 서버 조회
      }
    }
  }

  return fetchMyCurrentTeamMembershipFromServer(userId);
}

/**
 * 승인된 팀 소속(활성 여부 무관). RLS `can_read_workspace_learning_content`와 동일하게 status만 본다.
 * `getMyActiveTeamMembership`은 `is_active`가 있어야 해서, 승인만 된 팀원은 팀장 편제 조회에 실패할 수 있다.
 */
export async function getMyApprovedTeamForLearningShare(): Promise<Team | null> {
  const { data: authData } = await supabase.auth.getUser();
  const userId = authData.user?.id;
  if (!userId) return null;

  const { data, error } = await supabase
    .from('team_members')
    .select(
      'team:teams(id, workspace_id, created_by, name, code, member_limit, auto_approve_members)'
    )
    .eq('user_id', userId)
    .eq('status', 'approved')
    .order('is_active', { ascending: false })
    .order('joined_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    throw new Error(error.message);
  }
  if (!data?.team) return null;
  const resolvedTeam = Array.isArray(data.team) ? data.team[0] : data.team;
  return resolvedTeam ? (resolvedTeam as Team) : null;
}

/**
 * 승인된 팀 소속일 때 `teams.workspace_id`(팀장 워크스페이스).
 * 현재 보고 있는 워크스페이스가 그 id와 같으면 null — 팀장 본인 워크스페이스에서 중복 조회 방지.
 * 기본 학습내용 등록에서 팀원이 팀장 편제를 읽을 때 사용합니다.
 */
export async function getTeamLeaderWorkspaceIdForLearningShare(
  currentWorkspaceId: string | null | undefined
): Promise<string | null> {
  if (!currentWorkspaceId?.trim()) return null;
  const team = await getMyApprovedTeamForLearningShare();
  if (!team?.workspace_id) return null;
  const leaderWs = team.workspace_id;
  if (leaderWs === currentWorkspaceId) return null;
  return leaderWs;
}

export async function importTeamGradeTrack(
  teamId: string,
  targetSchoolId: string
): Promise<TeamShareImportResult> {
  const { data, error } = await supabase.rpc('import_team_grade_track', {
    p_team_id: teamId,
    p_target_school_id: targetSchoolId,
  });

  if (error) throw new Error(error.message);
  const row = Array.isArray(data) ? data[0] : data;
  const importedGrades = row?.imported_grades ?? 0;
  const importedTracks = row?.imported_tracks ?? 0;
  return { message: `학년 ${importedGrades}개, 편제 ${importedTracks}개를 가져왔습니다.` };
}

export async function importTeamCalendar(
  teamId: string,
  targetSchoolId: string
): Promise<TeamShareImportResult> {
  const { data, error } = await supabase.rpc('import_team_calendar', {
    p_team_id: teamId,
    p_target_school_id: targetSchoolId,
  });

  if (error) throw new Error(error.message);
  const row = Array.isArray(data) ? data[0] : data;
  const importedSettings = row?.imported_year_settings ?? 0;
  const importedTerms = row?.imported_terms ?? 0;
  const importedDays = row?.imported_days ?? 0;
  return {
    message: `학년도설정 ${importedSettings}건, 학기 ${importedTerms}건, 학사일정 ${importedDays}건을 가져왔습니다.`,
  };
}

export async function importTeamTimetable(
  teamId: string,
  targetSchoolId: string
): Promise<TeamShareImportResult> {
  const { data, error } = await supabase.rpc('import_team_timetable', {
    p_team_id: teamId,
    p_target_school_id: targetSchoolId,
  });

  if (error) throw new Error(error.message);
  const row = Array.isArray(data) ? data[0] : data;
  const importedBase = row?.imported_base_cells ?? 0;
  const importedOverride = row?.imported_override_cells ?? 0;
  return {
    message: `기초 일과운영 ${importedBase}건, 일자별 수업시수 ${importedOverride}건을 가져왔습니다.`,
  };
}

